import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";

interface Props {
  onSaved: () => void;
}

const OpeningBalanceDialog: React.FC<Props> = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("2026-08-01");

  const onOpenChange = async (v: boolean) => {
    setOpen(v);
    if (v) {
      setLoading(true);
      try {
        const res = await client.get("/cash/opening-balance");
        if (res.data.exists) {
          setAmount(String(res.data.amount));
          if (res.data.date) setDate(String(res.data.date).slice(0, 10));
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }
  };

  const submit = async () => {
    const val = parseFloat(amount);
    if (isNaN(val)) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSaving(true);
    try {
      await client.post("/cash/opening-balance", {
        amount: val,
        date: date ? `${date}T12:00:00Z` : undefined,
      });
      toast.success("Saldo inicial guardado");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          data-testid="opening-balance-button"
          className="flex h-9 items-center gap-2 rounded-none border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Wallet className="h-4 w-4" strokeWidth={2} />
          Saldo inicial
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Saldo inicial de caja</DialogTitle>
          <DialogDescription>
            El dinero (banco + efectivo) con el que ya contabas antes de empezar a registrar. Suma al saldo de caja pero NO cuenta como ingreso ni venta.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[#DD0000]" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Fecha de apertura</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="opening-balance-date"
                className="mt-1 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Saldo inicial (MXN)</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="opening-balance-amount"
                className="mt-1 rounded-none border-slate-300"
              />
              <p className="mt-1 text-[10px] text-slate-400">
                Saldo de caja = este saldo inicial + entradas registradas + depósitos de plataformas − salidas (desde esa fecha).
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          <button
            onClick={submit}
            disabled={saving || loading}
            data-testid="opening-balance-save"
            className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OpeningBalanceDialog;
